import React, { useEffect, useState } from "react";
import axios from "axios";
import "./Dashboard.css";
import { Card, CardContent, Typography, Grid, Tabs, Tab } from "@mui/material";
import { Calendar, User, Wallet } from "lucide-react";
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer, BarChart, Bar,
  PieChart, Pie, Cell, Sector
} from "recharts";
import { DateRange } from "react-date-range";
import DateRangePicker from "react-bootstrap-daterangepicker";
import DateRangePickerComponent from "../../CommonComponents/DateRangePickerComponent";
import QuickAndFullRangeDatePicker from "../../CommonComponents/QuickAndFullRangeDatePicker";
import ENDPOINTS from "../../Constants/ApiEndpoints";


const COLORS = ['#ff5a7b', '#4caf50', '#9c27d8', '#1976d2', '#ffc107',];


const renderActiveShape = (props) => {
  const { cx, cy, innerRadius, outerRadius, startAngle, endAngle, fill, payload } = props;
  return (
    <g>
      <Sector
        cx={cx}
        cy={cy}
        innerRadius={innerRadius}
        outerRadius={outerRadius + 8}
        startAngle={startAngle}
        endAngle={endAngle}
        fill={fill}
      />

    </g>
  );
};

const StatCard = ({ icon: Icon, value, label, trend, colorScheme }) => {
  const getScheme = () => {
    switch (colorScheme) {
      case 'green':
        return { bg: '#dcfce7', color: '#15803d', border: '#bbf7d0' };
      case 'indigo':
        return { bg: '#e0e7ff', color: '#4338ca', border: '#c7d2fe' };
      default:
        return { bg: '#e0f2fe', color: '#0369a1', border: '#bae6fd' };
    }
  };

  const scheme = getScheme();

  return (
    <div className="stat-card-enterprise">
      <div className="stat-header">
        <div className="stat-icon-wrapper" style={{ backgroundColor: scheme.bg, color: scheme.color, borderColor: scheme.border }}>
          <Icon size={22} />
        </div>
        {trend && <span className="stat-trend-badge">{trend}</span>}
      </div>

      <div className="stat-body">
        <h3 className="stat-value">{value}</h3>
        <p className="stat-label">{label}</p>
      </div>
    </div>
  );
};

const Dashboard = () => {
  const [selectedTab, setSelectedTab] = useState(0);

  const [ipCount, setIpCount] = useState(0);
  const [inpatientsCount, setIPpatientsCount] = useState(0);
  const [collectionAmount, setCollectionAmount] = useState(0);

  const [dailyAdmissionsData, setDailyAdmissionsData] = useState([]);

  const [doctorMetricsData, setDoctorMetricsData] = useState([]);
  const [admissionsTypeMetricData, setAdmissionsTypeMetricData] = useState([]);
  const [admissionsStatusMetricData, setAdmissionsStatusMetricData] = useState([]);
  const [activeIndex, setActiveIndex] = useState(null);
  const [windowWidth, setWindowWidth] = useState(window.innerWidth);


  useEffect(() => {
    const handleResize = () => setWindowWidth(window.innerWidth);
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  // Donut Chart Radius
  const getRadius = () => {
    if (windowWidth < 480) return { inner: 90, outer: 110 };
    if (windowWidth < 768) return { inner: 150, outer: 165 };
    return { inner: 150, outer: 165 };
  };

  const { inner, outer } = getRadius();


  // Chart Width (for bar or timeline charts)
  const getResponsiveChartWidth = (dailyAdmissionsData) => {
    if (windowWidth < 480) return Math.max(dailyAdmissionsData * 80, 300);
    if (windowWidth < 768) return Math.max(dailyAdmissionsData * 80, 1200);
    return Math.max(dailyAdmissionsData * 80, 1200);
  };


  // Donut states
  const [activeIndex1, setActiveIndex1] = useState(null);





  useEffect(() => {
    const today = new Date();
    fetchMetricsFromAPI(today, today);
    fetchDailyAdmissionCounts(today, today);
    fetchDoctorAdmissions(today, today);
    fetchAdmissionsTypeData(today, today);
    fetchAdmissionsStatusData(today, today);
    //handleDateRangeChange(today, today);
  }, []);

  function formatDateToLocalYMD(date) {
    return date.getFullYear() + '-' +
      String(date.getMonth() + 1).padStart(2, '0') + '-' +
      String(date.getDate()).padStart(2, '0');
  }

  const fetchMetricsFromAPI = async (start, end) => {
    // const fromDate = start.toISOString().split("T")[0];
    // const toDate = end.toISOString().split("T")[0];

    const fromDate = formatDateToLocalYMD(start);
    const toDate = formatDateToLocalYMD(end);
    // console.log('Raw start:', start.toString());
    // console.log('Formatted start:', formatDateToLocalYMD(start));


    try {
      const [ipRes, ippatientsRes, collectionRes] = await Promise.all([
        axios.get(ENDPOINTS.GET_TOTAL_IP_ADMISSIONS_COUNT_BY_DATE, { params: { fromDate: fromDate, toDate: toDate } }),
        axios.get(ENDPOINTS.GET_TOTAL_INPATIENTS_COUNT_BY_DATE, { params: { fromDate: fromDate, toDate: toDate } }),
        axios.get(ENDPOINTS.GET_TOTAL_COLLECTION, { params: { invoiceType: 'INPATIENT_INVOICE', fromDate: fromDate, toDate: toDate } }),
      ]);

      setIpCount(ipRes.data || 0);
      setIPpatientsCount(ippatientsRes.data || 0);
      setCollectionAmount(collectionRes.data || 0);
    } catch (error) {
      console.error("Error fetching metrics:", error);
      setIpCount(0);
      setIPpatientsCount(0);
      setCollectionAmount(0);
    }
  };



  const fetchDailyAdmissionCounts = async (start, end) => {
    const fromDate = formatDateToLocalYMD(start);
    const toDate = formatDateToLocalYMD(end);

    try {
      const response = await axios.get(ENDPOINTS.GET_DAILY_ADMISSIONS_COUNT, {
        params: { fromDate, toDate },
      });

      // API returns array of { date, freeCount, paidCount }
      setDailyAdmissionsData(response.data || []);
    } catch (error) {
      console.error("Error fetching daily admission counts:", error);
      setDailyAdmissionsData([]);
    }
  };


  const fetchDoctorAdmissions = async (start, end) => {
    const fromDate = formatDateToLocalYMD(start);
    const toDate = formatDateToLocalYMD(end);

    try {
      const response = await axios.get(ENDPOINTS.GET_TOTAL_IP_ADMISSIONS_COUNTOFDOCTOR_BYDATE, {
        params: { fromDate, toDate },
      });
      setDoctorMetricsData(response.data || []);
    } catch (error) {
      console.error("Error fetching doctor admissions:", error);
      setDoctorMetricsData([]);
    }
  };

  const fetchAdmissionsTypeData = async (start, end) => {
    const fromDate = formatDateToLocalYMD(start);
    const toDate = formatDateToLocalYMD(end);

    try {
      const response = await axios.get(ENDPOINTS.GET_ADMISSIONS_TREND_TYPEWISEBY_DATE, {
        params: { fromDate, toDate },
      });
      setAdmissionsTypeMetricData(response.data || []);
    } catch (error) {
      console.error("Error fetching doctor admissions:", error);
      setAdmissionsTypeMetricData([]);
    }
  };

  const fetchAdmissionsStatusData = async (start, end) => {
    const fromDate = formatDateToLocalYMD(start);
    const toDate = formatDateToLocalYMD(end);

    try {
      const response = await axios.get(ENDPOINTS.GET_ADMISSIONS_TREND_STATUSWISEBY_DATE, {
        params: { fromDate, toDate },
      });
      setAdmissionsStatusMetricData(response.data || []);
    } catch (error) {
      console.error("Error fetching doctor admissions:", error);
      setAdmissionsStatusMetricData([]);
    }
  };


  const handleTabChange = (event, newValue) => {
    setSelectedTab(newValue);
  };

  // const handleDateRangeChange = (start, end) => {
  //   setCustomDateRange({ start, end });

  // };

  const handleDateRangeChange = (start, end, callback) => {
    //setCustomDateRange({ start, end });
    callback(start, end);
  };


  //custom CustomTooltip to avoid big rectangle highlight when hovered on bar
  const CustomTooltip = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
      return (
        <div style={{
          backgroundColor: 'white',
          border: '1px solid #ccc',
          padding: '8px',
          borderRadius: '6px',
          boxShadow: '0 2px 6px rgba(0,0,0,0.1)',
        }}>
          <p style={{ margin: 0 }}><strong>{label}</strong></p>
          {payload.map((entry, index) => (
            <p key={index} style={{ margin: 0, color: entry.color }}>
              {entry.name}: {entry.value}
            </p>
          ))}
        </div>
      );
    }

    return null;
  };


  const AdmissionCustomTooltip = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
      const data = payload[0].payload;

      return (
        <div
          className="custom-tooltip"
          style={{
            background: '#fff',
            border: '1px solid #ccc',
            padding: '10px',
          }}
        >
          <p><strong>{label}</strong></p>
          <strong>
            <p style={{ color: '#ff5a7b' }}>Emergency: {data.emergency}</p>
            <p style={{ color: '#4caf50' }}>General: {data.general}</p>
          </strong>
        </div>
      );
    }
    return null;
  };






  return (
    <div className="dashboard-wrapper">
      {/* Dashboard Top Banner */}
      <div className="dashboard-hero-header">
        <div>
          <h2 className="dashboard-title">Inpatient Analytics & Clinical Overview</h2>
          <p className="dashboard-subtitle">Real-time IP Admissions & Ward Occupancy Metrics</p>
        </div>
        <div className="live-status-pill">
          <span className="live-dot" /> Live Data Sync
        </div>
      </div>

      {/* Date Range Selector Bar */}
      <div className="dashboard-filter-bar">
        <span className="filter-bar-label">📅 Metric Date Range:</span>
        <QuickAndFullRangeDatePicker
          onDateChange={(start, end) => handleDateRangeChange(start, end, fetchMetricsFromAPI)}
        />
      </div>

      {/* KPI Stats Grid */}
      <div className="stats-cards-grid">
        <StatCard
          icon={Calendar}
          value={ipCount}
          label="Total IP Admissions"
          trend="🏥 Active"
          colorScheme="blue"
        />
        <StatCard
          icon={User}
          value={inpatientsCount}
          label="Total Inpatients"
          trend="💳 Real-time"
          colorScheme="green"
        />
        <StatCard
          icon={Wallet}
          value={`₹ ${collectionAmount.toLocaleString()}`}
          label="Total IP Collection"
          trend="💳 Real-time"
          colorScheme="indigo"
        />
      </div>

      <div className="dashboard-tabs">
        <Tabs
          value={selectedTab}
          onChange={handleTabChange}
          indicatorColor="primary"
          textColor="primary"
        >
          <Tab label="Admission Trends" />
          <Tab label="Doctor Admission Trends" />
        </Tabs>
      </div>

      {selectedTab === 0 && (
        <div className="dashboard-chart">
          <Typography variant="h6" align="center" gutterBottom>
            Admission Trends - Daywise
          </Typography>

          <QuickAndFullRangeDatePicker
            onDateChange={(start, end) =>
              handleDateRangeChange(start, end, fetchDailyAdmissionCounts)
            }
          />
          <hr className="section-divider" />

          {dailyAdmissionsData.length === 0 ? (
            <Typography
              variant="body1"
              align="center"
              style={{ marginTop: 50 }}
            >
              No admission data available for the selected date range.
            </Typography>
          ) : (
            <div style={{ overflowX: 'auto', width: '100%' }}>
              <div
                style={{
                  width: getResponsiveChartWidth(dailyAdmissionsData.length),
                  minHeight: 300,
                }}
              >
                <ResponsiveContainer width="100%" height={300}>
                  <BarChart
                    data={dailyAdmissionsData.map(({ date, emergencyCount, generalCount }) => ({
                      date,
                      emergency: emergencyCount,
                      general: generalCount,
                    }))}
                    margin={{ top: 20, right: 30, left: 0, bottom: 5 }}
                  >
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="date" />
                    <YAxis />
                    <Tooltip content={<AdmissionCustomTooltip />} cursor={{ fill: 'transparent' }} />
                    <Legend />

                    <Bar dataKey="emergency" stackId="a" fill="#ff5a7b" name="Emergency" barSize={40} style={{ cursor: 'pointer' }} />
                    <Bar dataKey="general" stackId="a" fill="#4caf50" name="General" barSize={40} radius={[6, 6, 0, 0]} style={{ cursor: 'pointer' }} />
                  </BarChart>

                </ResponsiveContainer>
              </div>
            </div>
          )}

        </div>
      )
      }

      {/* DONUT CHARTS BEGIN */}
      {selectedTab === 0 && (
        <div className="donut-row">
          <div className="donut-chart-wrapper">
            <Typography variant="h6" align="center" gutterBottom>
              Admission Trends - Type wise
            </Typography>
            <QuickAndFullRangeDatePicker
              onDateChange={(start, end) => handleDateRangeChange(start, end, fetchAdmissionsTypeData)}
            />
            <hr className="section-divider" />
            <div className="donut-container">
              <ResponsiveContainer>
                <PieChart>
                  <Pie
                    data={admissionsTypeMetricData}
                    cx="50%"
                    cy="50%"
                    innerRadius={inner}
                    outerRadius={outer}
                    dataKey="value"
                    activeIndex={activeIndex}
                    activeShape={renderActiveShape}
                    onMouseEnter={(_, i) => setActiveIndex(i)}
                    onMouseLeave={() => setActiveIndex(null)}
                    paddingAngle={0}
                    cursor="pointer"
                  >
                    {admissionsTypeMetricData.map((_, i) => (
                      <Cell key={i} fill={COLORS[i % COLORS.length]} />
                    ))}
                  </Pie>
                  <text
                    x="50%"
                    y="50%"
                    textAnchor="middle"
                    dominantBaseline="middle"
                    fontSize="16"
                    fontWeight="bold"
                  >
                    {activeIndex === null
                      ? `Total: ${admissionsTypeMetricData.reduce((sum, item) => sum + item.value, 0)}`
                      : `${admissionsTypeMetricData[activeIndex].name}: ${admissionsTypeMetricData[activeIndex].value}`}
                  </text>
                  <Tooltip />
                  <Legend />
                </PieChart>
              </ResponsiveContainer>
            </div>
          </div>

          <div className="donut-chart-wrapper">
            <Typography variant="h6" align="center" gutterBottom>
              Admission Trends - Status wise
            </Typography>
            <QuickAndFullRangeDatePicker
              onDateChange={(start, end) => handleDateRangeChange(start, end, fetchAdmissionsStatusData)}
            />
            <hr className="section-divider" />
            <div className="donut-container">
              <ResponsiveContainer>
                <PieChart>
                  <Pie
                    data={admissionsStatusMetricData}
                    cx="50%"
                    cy="50%"
                    innerRadius={inner}
                    outerRadius={outer}
                    dataKey="value"
                    activeIndex={activeIndex1}
                    activeShape={renderActiveShape}
                    onMouseEnter={(_, i) => setActiveIndex1(i)}
                    onMouseLeave={() => setActiveIndex1(null)}
                    paddingAngle={0}
                    cursor="pointer"
                  >
                    {admissionsStatusMetricData.map((_, i) => (
                      <Cell key={i} fill={COLORS[i % COLORS.length]} />
                    ))}
                  </Pie>
                  <text
                    x="50%"
                    y="50%"
                    textAnchor="middle"
                    dominantBaseline="middle"
                    fontSize="16"
                    fontWeight="bold"
                  >
                    {activeIndex1 === null
                      ? `Total: ${admissionsStatusMetricData.reduce((sum, item) => sum + item.value, 0)}`
                      : `${admissionsStatusMetricData[activeIndex1].name}: ${admissionsStatusMetricData[activeIndex1].value}`}
                  </text>
                  <Tooltip />
                  <Legend />
                </PieChart>
              </ResponsiveContainer>
            </div>
          </div>
        </div>
      )}



      {selectedTab === 1 && (
        <div className="dashboard-chart">
          <Typography variant="h6" align="center" gutterBottom>
            Overall Doctor Admissions Trend
          </Typography>

          <QuickAndFullRangeDatePicker
            // onDateChange={handleDateRangeChangeForDoctorMetrics}
            onDateChange={(start, end) => handleDateRangeChange(start, end, fetchDoctorAdmissions)}

          />

          <hr className="section-divider" />

          {doctorMetricsData.length === 0 ? (
            <Typography variant="body1" align="center" style={{ marginTop: 50 }}>
              No Admission data available for the selected date range.
            </Typography>
          ) : (
            <ResponsiveContainer width="100%" height={380}>
              <BarChart
                data={doctorMetricsData.map(({ doctorName, admissionCount }) => ({
                  name: doctorName,
                  admissions: admissionCount,
                }))}
                margin={{ top: 20, right: 30, left: 20, bottom: 40 }}
                maxBarSize={50}
              >
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="name" angle={-45} textAnchor="end" interval={0} height={20} />
                <YAxis
                  label={{
                    value: 'Total OP Admissions', angle: -90, position: 'insideLeft',
                    offset: 5,
                    style: { textAnchor: 'middle', fill: '#666', fontSize: 14 }
                  }}
                />

                <Tooltip content={<CustomTooltip />} cursor={{ fill: 'transparent' }} />

                <Legend
                  layout="horizontal"
                  verticalAlign="bottom"
                  align="center"
                  wrapperStyle={{ paddingTop: 80, marginBottom: 20 }}
                />
                <Bar dataKey="admissions" fill="#3f51b5" name="Admissions" barSize={40} radius={[6, 6, 0, 0]} style={{ cursor: 'pointer' }} />
              </BarChart>
            </ResponsiveContainer>
          )}
        </div>
      )}

    </div>

  );
};

export default Dashboard;